// src/data/disciplineMemberActs.js
//
// T5 / D-9 (2026-10-15) - THE MEMBER'S OWN VOICE IN HIS DISCIPLINE CASE.
// Rulings 139 and 176 (data\YCP_WHAT_IS_LEFT_CLAIMS_AND_DISCIPLINE_2026-09-19.md D-9): the member named in a case reads
// it (ruling 176, firestore.rules namedMember()), and until now could put nothing on it. Three acts, his alone:
//   statement               his own account of what happened, in his words
//   instruction             what he instructs his representative to do (or not do)
//   representation_notice   notice that he chooses different representation (or to represent himself)
// disciplineCases/{id}/memberActs/{auto}: { kind, text, at, byUid, byName }. APPEND-ONLY - nothing is edited or
// deleted; a later act stands beside an earlier one. The date is the server's (the rules pin at == request.time).
// The Local Chairperson of the case's territory and the General Chairperson's office are told
// (functions\index.js onDisciplineMemberAct writes the notices from memberActNotices below).
//
// PURE. Projected into functions\shared\ by scripts\sync-shared-modules.js.

export const STATEMENT = 'statement';
export const INSTRUCTION = 'instruction';
export const REPRESENTATION_NOTICE = 'representation_notice';
export const MEMBER_ACT_KINDS = [STATEMENT, INSTRUCTION, REPRESENTATION_NOTICE];
export const KIND_LABELS = {
  statement: 'Statement',
  instruction: 'Instruction to my representative',
  representation_notice: 'Notice of different representation',
};
export const TEXT_MAX = 4000;
export const NOTICE_KIND = 'discipline_member_act';

const s = (v) => (v == null ? '' : String(v)).trim();

// The member named in the case, by the same two halves the rules use (ruling 176): his uid, or his verified e-mail.
// caller = { uid, role, email, emailVerified }.
export function isNamedMember(kase, caller) {
  const k = kase || {};
  const a = caller || {};
  if (s(a.role) !== 'member') return false;
  if (s(k.memberUid) && s(k.memberUid) === s(a.uid)) return true;
  return !!(s(k.memberEmail) && a.emailVerified === true && s(k.memberEmail).toLowerCase() === s(a.email).toLowerCase());
}

export function validateMemberAct(input, kase) {
  const i = input || {};
  const k = kase || {};
  const problems = [];
  const kind = s(i.kind);
  const text = typeof i.text === 'string' ? i.text.trim() : '';
  if (MEMBER_ACT_KINDS.indexOf(kind) === -1) problems.push('Choose a statement, an instruction or a notice of representation.');
  if (!text) problems.push('Write it before you record it.');
  else if (text.length > TEXT_MAX) problems.push('It is longer than ' + TEXT_MAX + ' characters.');
  if (!s(k.status) || s(k.status) === 'draft') problems.push('The case is not open yet.');
  return { ok: problems.length === 0, problems, value: { kind, text } };
}

// The notices for one act. Office-addressed (recipientUid null): the LC by the case's scopeKey and the GC's office by
// its generalCommittee. A blank address is returned in `unaddressed`, never silently dropped.
export function memberActNotices({ kase, caseId, act, at }) {
  const k = kase || {};
  const a = act || {};
  const label = s(k.caseNumber) || s(caseId).slice(0, 8).toUpperCase();
  const who = s(a.byName) || s(k.memberName) || 'The member';
  const title = (KIND_LABELS[a.kind] || 'Act') + ' from the member: ' + label;
  const body = who + ' recorded ' + (a.kind === STATEMENT ? 'a statement' : a.kind === INSTRUCTION ? 'an instruction to his representative' : 'a notice of different representation')
    + ' on discipline case ' + label + (s(at) ? ' on ' + s(at).slice(0, 10) : '') + ':\n\n' + s(a.text);
  const common = {
    kind: NOTICE_KIND, caseId: s(caseId), committee: s(k.generalCommittee), scopeKey: s(k.scopeKey),
    recipientUid: null, channel: 'in_app', title, body, actorName: who, actorRole: 'member',
    createdAt: s(at) || new Date(0).toISOString(), readAt: null,
  };
  const unaddressed = [];
  const records = [];
  if (s(k.scopeKey)) records.push(Object.assign({}, common, { audience: 'local_chair' }));
  else unaddressed.push('local_chair: the case carries no territory');
  if (s(k.generalCommittee)) records.push(Object.assign({}, common, { audience: 'general_chair' }));
  else unaddressed.push('general_chair: the case carries no committee');
  return { records, unaddressed };
}
